import { type CSSProperties, useCallback, useState } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb } from '../adapters/powersync';
import { useSyncStatus } from './useSyncStatus';
import { retrySync } from './syncActions';

/**
 * Session-expired (JWT) modal.
 *
 * When the sync token lapses, PowerSync closes the stream with
 * `PSYNC_S2103 — JWT has expired`. Unlike a stuck UPLOAD (handled by
 * StuckSyncModal), this is a download-only failure that usually carries ZERO
 * pending operations, so nothing warned the user — the page just silently
 * showed stale data. This modal catches that case on any page and offers to
 * reconnect (refresh the auth token + restart the sync stream) or reload.
 *
 * It auto-dismisses the moment sync recovers (`authExpired` flips false).
 */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1001,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--sp-4, 16px)',
};

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: 'var(--bg-surface, #fff)',
  borderRadius: 'var(--radius-lg, 14px)',
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
  padding: 'var(--sp-6, 24px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4, 16px)',
};

const primaryBtn: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--radius-md, 10px)',
  border: 'none',
  background: 'var(--primary, #2563eb)',
  color: '#fff',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-semi, 600)',
  cursor: 'pointer',
};

const secondaryBtn: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  background: 'var(--bg-surface, #fff)',
  color: 'var(--text, #0f172a)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  cursor: 'pointer',
};

const ghostBtn: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-md, 10px)',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted, #64748b)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  cursor: 'pointer',
};

export function SyncReauthModal() {
  const { db, connector } = usePowerSyncDb();
  const { getIdToken } = useAuth();
  const health = useSyncStatus();

  const [dismissed, setDismissed] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleReconnect = useCallback(async () => {
    if (!db || !connector) {
      window.location.reload();
      return;
    }
    setReconnecting(true);
    setFailed(false);
    try {
      // Force a fresh Firebase ID token so fetchCredentials hands PowerSync a
      // valid, non-expired JWT, then restart the sync stream.
      await getIdToken(true);
      await retrySync(db, connector);
      // Give the stream a moment to re-establish; the modal auto-hides via
      // `authExpired` once `connected` flips true.
      await new Promise((r) => setTimeout(r, 2_500));
    } catch (err) {
      console.error('[SyncReauthModal] Reconnect failed:', err);
      setFailed(true);
    } finally {
      setReconnecting(false);
    }
  }, [db, connector, getIdToken]);

  // Show only on a live session-expiry, and only until sync recovers.
  if (!health.authExpired || dismissed) return null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="sync-reauth-title">
      <div style={cardStyle}>
        <h2
          id="sync-reauth-title"
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg, 18px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            color: 'var(--text, #0f172a)',
          }}
        >
          Your session expired
        </h2>

        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm, 14px)',
            lineHeight: 1.6,
            color: 'var(--text-muted, #475569)',
          }}
        >
          This device stopped receiving updates because your secure session timed out, so the
          information shown here may be out of date. Reconnect to load the latest data. Anything you
          saved on this device is safe.
        </p>

        {failed && (
          <div
            style={{
              padding: 'var(--sp-3, 12px)',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 'var(--radius-md, 10px)',
              fontSize: 'var(--font-size-xs, 12px)',
              color: '#991b1b',
              lineHeight: 1.5,
            }}
          >
            Couldn’t reconnect automatically. Please reload the page to restore the connection.
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3, 12px)' }}>
          <button type="button" onClick={handleReconnect} disabled={reconnecting} style={primaryBtn}>
            {reconnecting ? 'Reconnecting…' : 'Reconnect'}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            disabled={reconnecting}
            style={secondaryBtn}
          >
            Reload page
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setDismissed(true)} style={ghostBtn}>
            Dismiss for now
          </button>
        </div>
      </div>
    </div>
  );
}
